import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import topicsStyle from "../../Topics.styles";
import { BootstrapInput } from "../../../../utils/styles";
import { useEffect, useState } from "react";
import {
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
} from "../../../../helpers/methods";
import notifiers from "../../../../global/constants/NotificationConstants";
import { deleteBlog, fetchBlogList, getTopicData } from "../../Topics.service";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonIcon from "@mui/icons-material/Person";
import SourceIcon from "@mui/icons-material/Source";
import CustomLoader from "../../../../global/components/CustomLoader/CustomLoader";
import history from "../../../../utils/history";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useAppSelector } from "../../../../utils/hooks";
import { isAdmin } from "../../../../redux/authSlice";

const AllBlogs = () => {
  const isUserAdmin = useAppSelector(isAdmin);
  const [dataPerPage, setDataPerPage] = useState<number>(12);
  const [totalPage, setTotalPage] = useState<number>(0);
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState<number>(1);
  const topic = window.location.pathname.split("/")[3] ?? "";
  const [blogs, setBlogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const classes = topicsStyle;

  useEffect(() => {
    getBlogList();
  }, [topic, page, dataPerPage]);

  const getBlogList = async () => {
    try {
      setBlogs([]);
      setIsLoading(true);
      const response = await fetchBlogList(topic, dataPerPage, page);
      setBlogs(response.blogs);
      setTotalPage(response.totalPages);
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      openErrorNotification(
        isTruthy(error.errorMessage)
          ? error.errorMessage
          : notifiers.GENERIC_ERROR
      );
    }
  };

  const handleChange = (event: any) => {
    setDataPerPage(event.target.value);
  };

  const handleChangePage = (
    event: React.ChangeEvent<unknown> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const deleteBlogById = async (blogId: string) => {
    try {
      setIsLoading(true);
      await deleteBlog(blogId);
      setIsLoading(false);
      getBlogList();
      openSuccessNotification("Blog deleted successfully!");
    } catch (error: any) {
      setIsLoading(false);
      openErrorNotification(
        isTruthy(error.errorMessage)
          ? error.errorMessage
          : notifiers.GENERIC_ERROR
      );
    }
  };

  return (
    <>
      <Box>
        <Typography
          variant="h5"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textDecoration: "underline",
          }}
          pt={2}
        >
          {`Blog list (${topic.replace("-", " ")})`}
        </Typography>
        <Grid container justifyContent={"center"}>
          <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
            <Grid container spacing={3} justifyContent={"center"} p={4} pt={2}>
              {blogs.map((blog: any, index: number) => {
                return (
                  <Grid item xs={12} key={`blog-${index}`}>
                    <Card
                      sx={{
                        width: "100%",
                        height: "fit-content",
                        boxShadow: 2,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                      }}
                    >
                      <CardContent
                        sx={{ p: 2, pb: "16px !important", flex: 1 }}
                      >
                        <Typography
                          variant="h6"
                          component="h2"
                          fontWeight="bold"
                          sx={{
                            lineHeight: 1.2,
                            mb: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: "vertical",
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            history.push({
                              pathname: `/blog/${blog.blog_id}/${blog.headline}`,
                              state: { id: blog.id, title: blog.headline },
                            })
                          }
                        >
                          {index + 1}. {blog.headline}
                        </Typography>

                        <Divider sx={{ my: 1 }} />

                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                            mt: 1,
                          }}
                        >
                          {blog.date && (
                            <Chip
                              icon={<CalendarTodayIcon />}
                              label={blog.date}
                              variant="outlined"
                              size="small"
                              sx={{ height: "24px" }}
                            />
                          )}

                          {blog.author && (
                            <Chip
                              icon={<PersonIcon />}
                              label={blog.author}
                              variant="outlined"
                              size="small"
                              sx={{ height: "24px" }}
                            />
                          )}

                          {blog.source && (
                            <Chip
                              icon={<SourceIcon />}
                              label={blog.source}
                              variant="outlined"
                              size="small"
                              sx={{ height: "24px" }}
                            />
                          )}
                          {isUserAdmin && (
                            <Tooltip title={"Delete"} arrow>
                              <Box
                                onClick={() => deleteBlogById(blog.blog_id)}
                                style={{ color: "black", cursor: "pointer" }}
                              >
                                <DeleteOutlineIcon />
                              </Box>
                            </Tooltip>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
              <Grid
                item
                xl={12}
                lg={12}
                md={12}
                sm={12}
                xs={12}
                sx={classes.paginationWrapper}
              >
                <FormControl sx={{ m: 1 }} variant="standard">
                  <InputLabel>Blogs per page</InputLabel>
                  <Select
                    labelId="demo-customized-select-label"
                    id="demo-customized-select"
                    value={dataPerPage}
                    onChange={handleChange}
                    input={<BootstrapInput />}
                    defaultValue={12}
                  >
                    <MenuItem value={9}>9</MenuItem>
                    <MenuItem value={12}>12</MenuItem>
                    <MenuItem value={15}>15</MenuItem>
                  </Select>
                </FormControl>
                <Pagination
                  sx={classes.pagination}
                  showFirstButton
                  showLastButton
                  count={totalPage}
                  color="primary"
                  page={page}
                  onChange={handleChangePage}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
      <CustomLoader isLoading={isLoading} />
    </>
  );
};

export default AllBlogs;
